import React, { useEffect, useState } from "react";
import { X, Save, Loader2, CheckCircle2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

/** Right-side slide-over form for editing a doctor. Matches the reference screenshot. */
export default function DoctorEditDrawer({ doctor, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setD(doctor ? {
      name: doctor.name || "",
      email: doctor.email || "",
      mobile: doctor.mobile || "",
      register_no: doctor.register_no || "",
      state_medical_council: doctor.state_medical_council || "",
      year_of_registration: doctor.year_of_registration || "",
      belongs_to_our_circle: !!doctor.belongs_to_our_circle,
      is_chief_doctor: !!doctor.is_chief_doctor,
      is_blocked: !!doctor.is_blocked,
    } : null);
  }, [doctor]);

  if (!doctor || !d) return null;

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/doctors/${doctor.id}`, d);
      toast.success("Doctor updated");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="doctor-edit-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#1F2A24]/40" onClick={onClose} />
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EFECE5]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8A9992]">Doctor</div>
            <h3 className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>{doctor.name || "Unnamed doctor"}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center text-[#8A9992]" data-testid="drawer-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <Field label="Name" required>
            <input className="input-clean" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid="edit-name" />
          </Field>
          <Field label="Email" required>
            <input className="input-clean" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} data-testid="edit-email" />
          </Field>
          <Field label="Mobile Number" required>
            <input className="input-clean" value={d.mobile} onChange={(e) => setD({ ...d, mobile: e.target.value })} placeholder="+91 …" data-testid="edit-mobile" />
          </Field>

          <div className="space-y-3 pt-2">
            <Checkbox
              label="Belongs to Our Circle"
              checked={d.belongs_to_our_circle}
              onChange={(v) => setD({ ...d, belongs_to_our_circle: v })}
              testId="edit-circle"
            />
            <Checkbox
              label="Chief Doctor"
              checked={d.is_chief_doctor}
              onChange={(v) => setD({ ...d, is_chief_doctor: v })}
              testId="edit-chief"
            />
          </div>

          <Field label="Register No" required>
            <input className="input-clean" value={d.register_no} onChange={(e) => setD({ ...d, register_no: e.target.value })} data-testid="edit-register" />
          </Field>
          <Field label="State Medical Council Name" required>
            <input className="input-clean" value={d.state_medical_council} onChange={(e) => setD({ ...d, state_medical_council: e.target.value })} data-testid="edit-council" />
          </Field>
          <Field label="Year Of Registration" required>
            <input className="input-clean" value={d.year_of_registration} onChange={(e) => setD({ ...d, year_of_registration: e.target.value })} placeholder="2010" data-testid="edit-year" />
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-[#EFECE5] flex gap-3">
          <button onClick={save} disabled={busy} className="btn-emerald flex-1 justify-center" data-testid="drawer-update">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {busy ? "Saving…" : "Update"}
          </button>
          <button onClick={onClose} className="btn-outline-emerald flex-1 justify-center" data-testid="drawer-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1.5">
        {label} {required && <span className="text-[#B14A2A]">*</span>}
      </div>
      {children}
    </label>
  );
}

function Checkbox({ label, checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-3"
      data-testid={testId}
    >
      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${checked ? "bg-[#0A4C36] border-[#0A4C36]" : "bg-white border-[#B5C0BA]"}`}>
        {checked && <CheckCircle2 size={14} className="text-white" strokeWidth={2.5} />}
      </span>
      <span className="text-sm text-[#1F2A24]">{label}</span>
    </button>
  );
}
